import { CardImage } from '@components/Card';
import {
  ContactDetailList,
  LinksWrapper,
  LinkText,
  PropertyCooperativeAgencyStatus,
  PropertyListingHeaderWrapper,
  PropertyListingStatusWrapper,
  StyledCardContentDetailList,
  StyledLogoWrapper,
} from '@components/Card/PropertyListingCard/style';
import {
  CardContentWrapper,
  CardHeaderStyled,
  CardHeaderWrapper,
  CardWrapper,
  EmptyCardImage,
} from '@components/Card/style';
import EVImg from '@components/EVImg';
import ConditionallyWrapWithUrl from '@components/MiscComponents/ConditionallyWrapWithUrl';
import { FavoriteButton } from '@components/MiscComponents/FavoriteButton';
import { Listing, PropertyTypeEnum } from '@engelvoelkersamericas/api';
import { useCurrency } from '@util/customHooks/useCurrency';
import { useMeasurement } from '@util/customHooks/useMeasurement';
import usePropertySaver from '@util/customHooks/usePropertySaver';
import { getCorrectAreaToDisplay } from '@util/formatArea';
import { buildListUrl, processListingStatus } from '@util/listings';
import { getCompiledAddress } from '@util/string';
import { observer } from 'mobx-react-lite';
import { useStores } from 'mobx/useStores';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import camelCase from 'lodash/camelCase';
import { FC, useMemo } from 'react';
import { getSqFt } from '@util/propertiesMapping';

const BAHAMAS_SHOP = 'Engel & Völkers Bahamas';

interface PropertyCardProps {
  listing: Listing;
  canFavorite?: boolean;
  dataTestId?: string;
  idx?: number;
  refreshProperties?: ({
    listingId,
    listOfficeMlsId,
    remove,
  }: {
    listingId: string;
    listOfficeMlsId: string;
    remove: boolean;
  }) => void;
}

const PropertyCard: FC<PropertyCardProps> = observer(
  ({ listing, canFavorite = true, refreshProperties, dataTestId, idx }) => {
    const t = useTranslations();
    const { isFav, onSaveProperty } = usePropertySaver({
      listingId: listing.ListingId,
      listOfficeMlsId: listing.ListOfficeMlsId ?? '',
    });
    const { appStore } = useStores();
    const { validSubdomainData, mlsComplianceData } = appStore;
    const { convertArea } = useMeasurement();
    const { getListingPrice } = useCurrency();
    const {
      PropertyType,
      PropertySubType,
      BedroomsTotal,
      BathroomsTotal,
      LotSizeAcres,
      LotSizeSquareFeet,
      LivingArea,
      LivingAreaUnits,
      OriginatingSystemName,
      ListOfficeName,
      ListAgentFullName,
      City,
      Country,
      StateOrProvince,
      PostalCode,
      StreetNumber,
      StreetName,
      ListingId,
      ListPrice,
      ClosePrice,
      ClosePriceToDisplay,
      OpenHouse,
      MainPhotoUrl,
      StandardStatus,
      CurrencyCode,
      AttributionContact,
      BuyerAgentFullName,
      BuyerOfficeName,
      CooperativeAgencyYN,
      CooperativeAgencyName,
      LeaseAmount,
      LeaseAmountFrequency,
    } = listing;

    const sqFt = getSqFt(listing);

    const areaToDisplay = getCorrectAreaToDisplay(
      convertArea,
      t,
      PropertySubType,
      LotSizeAcres,
      sqFt,
    );

    const listingUrl = useMemo(() => {
      const listingPath = buildListUrl({
        StreetNumber: StreetNumber,
        StreetName: StreetName,
        City: City,
        StateOrProvince: StateOrProvince,
        ZipCode: PostalCode,
        OriginatingSystemName: OriginatingSystemName,
        ListingId: ListingId,
      });

      // If we are on an advisor page, we need to navigate to the shop's listing page
      // by adding the shop's subdomain to the listing path
      if (validSubdomainData) {
        const subdomainType = validSubdomainData.type?.toLowerCase();
        if (subdomainType && ['advisor', 'team'].includes(subdomainType)) {
          return `${window.location.protocol}//${validSubdomainData.shopSubdomain}.${validSubdomainData.domain}${listingPath}`;
        }
      }

      return listingPath;
    }, [listing, validSubdomainData]);

    const { formattedPrice } = getListingPrice({
      standardStatus: StandardStatus,
      listPrice: ListPrice!,
      closePrice: ClosePrice,
      closePriceToDisplay: ClosePriceToDisplay,
      originatingSystemName: OriginatingSystemName,
      country: Country,
      listingCurrency: CurrencyCode,
      leaseAmount: LeaseAmount,
      leaseAmountFrequency: LeaseAmountFrequency,
      propertyType: PropertyType,
    });

    const osnComplianceData = mlsComplianceData?.[OriginatingSystemName ?? ''];

    const listingStatusProcessed = useMemo(() => {
      return processListingStatus(OpenHouse, StandardStatus, PropertyType);
    }, [OpenHouse, StandardStatus]);

    const isCooperativeAgency = CooperativeAgencyYN ?? false;
    const compiledAddress = getCompiledAddress(listing); // unparsed address is preferred, this is a fallback

    const getListingOfficeName = () => {
      if (CooperativeAgencyYN && CooperativeAgencyName) {
        return CooperativeAgencyName;
      }
      return ListOfficeName?.replace('V?lkers', 'Völkers');
    };

    const listOfficeUrl: string | null = null;
    const isOfficeNameValid = (CooperativeAgencyYN && CooperativeAgencyName) || ListOfficeName;
    const complianceText = isOfficeNameValid && (
      <>
        <>{t('Listing Courtesy of')} </>
        {listOfficeUrl === '' ? (
          <Link href={listOfficeUrl}>{getListingOfficeName()}</Link>
        ) : (
          <>{getListingOfficeName()}</>
        )}
      </>
    );

    const nerenText = (
      <>
        {ListAgentFullName && `, ${ListAgentFullName}`}
        {AttributionContact && `, ${AttributionContact}`}
        <br />
        {BuyerAgentFullName && `Sold by ${BuyerAgentFullName}`}
        {BuyerOfficeName && `, ${BuyerOfficeName}`}
      </>
    );

    const lotSize = LotSizeAcres
      ? `${LotSizeAcres} ${t('Acres')}`
      : LotSizeSquareFeet && `${convertArea(LotSizeSquareFeet, 'sq.ft').formattedArea}`;

    const priceUponRequest = listing?.PriceUponRequest ? t('Price Upon Request') : undefined;

    return (
      <CardWrapper className="property-listing-card" data-testid={dataTestId}>
        {MainPhotoUrl ? ( //first check if array exists and has length
          <ConditionallyWrapWithUrl href={listingUrl}>
            <CardImage
              dataTestId={`property-listing-img-${idx}`}
              image={MainPhotoUrl}
              alt_text="property"
              imageAspectRatio={0.75}
            />
          </ConditionallyWrapWithUrl>
        ) : (
          <ConditionallyWrapWithUrl href={listingUrl}>
            <EmptyCardImage />
          </ConditionallyWrapWithUrl>
        )}

        <PropertyListingHeaderWrapper>
          {formattedPrice && (
            <CardHeaderWrapper>
              <ConditionallyWrapWithUrl href={listingUrl}>
                <CardHeaderStyled data-testid={`property-listing-price-${idx}`}>
                  {priceUponRequest ?? formattedPrice}
                </CardHeaderStyled>
              </ConditionallyWrapWithUrl>

              {canFavorite && isFav !== null && (
                <FavoriteButton
                  dataTestId={`property-listing-favorite-btn-${idx}`}
                  $isFavorite={isFav}
                  onClick={() => onSaveProperty(refreshProperties)}
                />
              )}
            </CardHeaderWrapper>
          )}
        </PropertyListingHeaderWrapper>

        <CardContentWrapper>
          {listingStatusProcessed && (
            <PropertyListingStatusWrapper>{listingStatusProcessed}</PropertyListingStatusWrapper>
          )}

          {isCooperativeAgency && (
            <PropertyCooperativeAgencyStatus>
              {t('Cooperative Agency')}
            </PropertyCooperativeAgencyStatus>
          )}

          <StyledCardContentDetailList>
            {compiledAddress && (
              <li data-testid={`property-listing-address-${idx}`}> {compiledAddress} </li>
            )}

            <li>
              <ContactDetailList>
                {PropertyType === PropertyTypeEnum.Land || !PropertySubType ? (
                  <li data-testid={`property-listing-type-${idx}`}>
                    {t(`PropertyTypes.${camelCase(PropertyType)}` as any)}
                  </li>
                ) : null}
                {!!PropertySubType && PropertyType !== PropertyTypeEnum.Land ? (
                  <li data-testid={`property-listing-subtype-${idx}`}>
                    {t(`PropertySubTypes.${camelCase(PropertySubType)}` as any)}
                  </li>
                ) : null}
                {BedroomsTotal && (
                  <li data-testid={`property-listing-bedrooms-${idx}`}>
                    {BedroomsTotal} {t('Beds')}
                  </li>
                )}
                {BathroomsTotal && (
                  <li data-testid={`property-listing-bathrooms-${idx}`}>
                    {BathroomsTotal} {t('Baths')}
                  </li>
                )}
                {PropertyType === PropertyTypeEnum.Land && lotSize ? (
                  <li data-testid={`property-listing-size-${idx}`}>{lotSize}</li>
                ) : (
                  areaToDisplay && (
                    <li data-testid={`property-listing-living-area-${idx}`}>{areaToDisplay}</li>
                  )
                )}
              </ContactDetailList>
            </li>
          </StyledCardContentDetailList>

          {/* we want only an explicit `false` to hide this, `undefined` should show it */}
          {/* never show compliance text for the Bahamas shop */}
          {osnComplianceData &&
            BAHAMAS_SHOP !== ListOfficeName &&
            OriginatingSystemName &&
            osnComplianceData.includeListingCourtesyOf !== false && (
              <LinksWrapper>
                <span>
                  <LinkText data-testid={`listing-provider-${idx}`}>
                    {complianceText}
                    {OriginatingSystemName === 'NEREN' ? nerenText : null}
                  </LinkText>
                </span>

                {osnComplianceData.img?.fields.file?.url && (
                  <>
                    <StyledLogoWrapper data-testid={`compliance-logo-${idx}`}>
                      <EVImg
                        src={osnComplianceData.img?.fields.file?.url}
                        imageDesc={
                          osnComplianceData.name ? osnComplianceData.name : 'Compliance Logo'
                        }
                        priority={true}
                        layout="fill"
                        objectFit="contain"
                        unoptimized={true}
                      />
                    </StyledLogoWrapper>
                  </>
                )}
              </LinksWrapper>
            )}
        </CardContentWrapper>
      </CardWrapper>
    );
  },
);

export default PropertyCard;
